import React, { useEffect, useMemo, useState } from 'react';
import EngagerSliderRow from './calc_components/EngagerSliderRow';
import EngagerSelectRow from './calc_components/EngagerSelectRow';
import CalculatorToolsRail from '../components/CalculatorToolsRail';
import CalculatorPrecisionToggle from './calc_components/CalculatorPrecisionToggle';
import { formatYearsCount } from './calc_components/calculatorFormatters';
import { computeEmi } from './EmiCalculator';
import {
  loanFromEmi,
  projectHomeLoanAffordability,
} from './homeLoanAffordabilityMath';
import SurplusWaterChart from './SurplusWaterChart';
import {
  defaultLoanAmount,
  getDefaults,
  maxIncomeGrowth,
  maxExpenseGrowth,
  incomeGrowthStep,
  pctStep,
  minPct,
  minInterestRate,
  maxInterestRate,
  interestRateStep,
  minTenureYears,
  maxTenureYears,
  minLifeEventYear,
  maxLifeEventYearOptions,
  maxMoney,
  moneyStep,
  maxEmi,
  emiStep,
  POST_MOVE_CHANGE_OPTIONS,
  LIFE_CHANGE_OPTIONS,
  MOVE_IN_OPTIONS,
  LIFE_EVENT_YEAR_OPTIONS,
  formatPct,
  formatInterestRatePct,
  snapInterestRate,
  snapPct,
  ModeSwitchLink,
  MoneyField,
  SelectWithInlineAmount,
  SectionHeading,
} from './homeLoanAffordabilityShared';
import './calculators.css';
import './HomeLoanAffordabilityCharts.css';

export default function HomeLoanAffordabilityCalculator({ embed = false }) {
  useEffect(() => {
    if (!embed) window.scrollTo(0, 0);
  }, [embed]);

  const defaults = getDefaults(embed);
  const [annualTakeHome, setAnnualTakeHome] = useState(defaults.annualTakeHome);
  const [annualExpenses, setAnnualExpenses] = useState(defaults.annualExpenses);
  const [incomeGrowthPct, setIncomeGrowthPct] = useState(defaults.incomeGrowthPct);
  const [expenseGrowthPct, setExpenseGrowthPct] = useState(defaults.expenseGrowthPct);
  const [moveInYears, setMoveInYears] = useState(defaults.moveInYears);
  const [postMoveExpenseChange, setPostMoveExpenseChange] = useState(
    defaults.postMoveExpenseChange,
  );
  const [postMoveExpenseAmount, setPostMoveExpenseAmount] = useState(
    defaults.postMoveExpenseAmount,
  );
  const [hasLifeEvent, setHasLifeEvent] = useState(defaults.hasLifeEvent);
  const [lifeEventYear, setLifeEventYear] = useState(defaults.lifeEventYear);
  const [lifeEventIncomeChange, setLifeEventIncomeChange] = useState(
    defaults.lifeEventIncomeChange,
  );
  const [lifeEventIncomeAmount, setLifeEventIncomeAmount] = useState(
    defaults.lifeEventIncomeAmount,
  );
  const [lifeEventExpenseChange, setLifeEventExpenseChange] = useState(
    defaults.lifeEventExpenseChange,
  );
  const [lifeEventExpenseAmount, setLifeEventExpenseAmount] = useState(
    defaults.lifeEventExpenseAmount,
  );
  const [tenureYears, setTenureYears] = useState(defaults.tenureYears);
  const [interestRatePct, setInterestRatePct] = useState(defaults.interestRatePct);
  const [loanSizingMode, setLoanSizingMode] = useState(defaults.loanSizingMode);
  const [monthlyEmi, setMonthlyEmi] = useState(defaults.monthlyEmi);
  const [loanAmount, setLoanAmount] = useState(defaults.loanAmount);

  const [takeHomeWords, setTakeHomeWords] = useState(defaults.annualTakeHome);
  const [expensesWords, setExpensesWords] = useState(defaults.annualExpenses);
  const [postMoveWords, setPostMoveWords] = useState(defaults.postMoveExpenseAmount);
  const [lifeIncWords, setLifeIncWords] = useState(defaults.lifeEventIncomeAmount);
  const [lifeExpWords, setLifeExpWords] = useState(defaults.lifeEventExpenseAmount);
  const [emiWords, setEmiWords] = useState(defaults.monthlyEmi);
  const [loanWords, setLoanWords] = useState(defaults.loanAmount);

  const maxLifeYear = Math.max(minLifeEventYear, tenureYears);

  useEffect(() => {
    const capped = Math.min(
      maxLifeEventYearOptions,
      Math.max(minLifeEventYear, lifeEventYear),
    );
    if (capped !== lifeEventYear) setLifeEventYear(capped);
  }, [lifeEventYear]);

  function handleStartOver(e) {
    e.preventDefault();
    const d = getDefaults(embed);
    setAnnualTakeHome(d.annualTakeHome);
    setAnnualExpenses(d.annualExpenses);
    setIncomeGrowthPct(d.incomeGrowthPct);
    setExpenseGrowthPct(d.expenseGrowthPct);
    setMoveInYears(d.moveInYears);
    setPostMoveExpenseChange(d.postMoveExpenseChange);
    setPostMoveExpenseAmount(d.postMoveExpenseAmount);
    setHasLifeEvent(d.hasLifeEvent);
    setLifeEventYear(d.lifeEventYear);
    setLifeEventIncomeChange(d.lifeEventIncomeChange);
    setLifeEventIncomeAmount(d.lifeEventIncomeAmount);
    setLifeEventExpenseChange(d.lifeEventExpenseChange);
    setLifeEventExpenseAmount(d.lifeEventExpenseAmount);
    setTenureYears(d.tenureYears);
    setInterestRatePct(d.interestRatePct);
    setLoanSizingMode(d.loanSizingMode);
    setMonthlyEmi(d.monthlyEmi);
    setLoanAmount(d.loanAmount);
    setTakeHomeWords(d.annualTakeHome);
    setExpensesWords(d.annualExpenses);
    setPostMoveWords(d.postMoveExpenseAmount);
    setLifeIncWords(d.lifeEventIncomeAmount);
    setLifeExpWords(d.lifeEventExpenseAmount);
    setEmiWords(d.monthlyEmi);
    setLoanWords(d.loanAmount);
  }

  const showPostMoveAmount = postMoveExpenseChange !== 'No change';
  const showLifeIncAmount = hasLifeEvent && lifeEventIncomeChange !== 'No change';
  const showLifeExpAmount = hasLifeEvent && lifeEventExpenseChange !== 'No change';

  const sizeByEmi = loanSizingMode === 'emi';
  const resolvedMonthlyEmi = useMemo(() => {
    if (sizeByEmi) return Math.max(0, Number(monthlyEmi) || 0);
    return Math.round(computeEmi(loanAmount, interestRatePct, tenureYears));
  }, [sizeByEmi, monthlyEmi, loanAmount, interestRatePct, tenureYears]);

  const resolvedLoanAmount = useMemo(() => {
    if (!sizeByEmi) return Math.max(0, Number(loanAmount) || 0);
    return Math.round(loanFromEmi(monthlyEmi, interestRatePct, tenureYears));
  }, [sizeByEmi, loanAmount, monthlyEmi, interestRatePct, tenureYears]);

  function handleLoanSizingModeChange(nextMode) {
    if (nextMode === loanSizingMode) return;
    if (nextMode === 'loan') {
      setLoanAmount(defaultLoanAmount);
      setLoanWords(defaultLoanAmount);
    } else {
      const nextEmi = Math.round(
        computeEmi(loanAmount, interestRatePct, tenureYears),
      );
      setMonthlyEmi(nextEmi);
      setEmiWords(nextEmi);
    }
    setLoanSizingMode(nextMode);
  }

  const projection = useMemo(
    () =>
      projectHomeLoanAffordability({
        annualTakeHome,
        annualExpenses,
        incomeGrowthPct,
        expenseGrowthPct,
        moveInYears,
        postMoveExpenseChange,
        postMoveExpenseAmount,
        hasLifeEvent,
        lifeEventYear: Math.min(lifeEventYear, maxLifeYear),
        lifeEventIncomeChange,
        lifeEventIncomeAmount,
        lifeEventExpenseChange,
        lifeEventExpenseAmount,
        monthlyEmi: resolvedMonthlyEmi,
        interestRatePct,
        tenureYears,
        horizonYears: 10,
      }),
    [
      annualTakeHome,
      annualExpenses,
      incomeGrowthPct,
      expenseGrowthPct,
      moveInYears,
      postMoveExpenseChange,
      postMoveExpenseAmount,
      hasLifeEvent,
      lifeEventYear,
      maxLifeYear,
      lifeEventIncomeChange,
      lifeEventIncomeAmount,
      lifeEventExpenseChange,
      lifeEventExpenseAmount,
      resolvedMonthlyEmi,
      interestRatePct,
      tenureYears,
    ],
  );

  const calculatorCard = (
    <div
      className={`appContainer nps-ups--compact home-loan-affordability${
        embed ? ' nps-ups--embed' : ''
      }`}
    >
      <h1 className="tool-heading">Home Loan Affordability Stress-Tester</h1>

      <div className="col-light-grey italic font-size-small nps-ups-intro">
        <p>
          See how your take-home pay, expenses, income growth, life events, and other
          factors affect loan affordability. Stress-test your EMI capacity and estimate
          how much you can safely borrow.
        </p>
      </div>

      <section className="hla-section hla-section--band-b" aria-labelledby="hla-sec-income">
        <SectionHeading
          tipId="hla-sec-income-tip"
          tooltip="Your annual take-home pay is the income credited to your bank account after taxes, PF, and other mandatory deductions. It is not your CTC or the salary stated in your offer letter. If you and your spouse both work, enter your combined annual take-home pay."
        >
          <span id="hla-sec-income">
            Tell us about your current household take-home pay
          </span>
        </SectionHeading>
        <div className="hla-inputs-grid">
          <MoneyField
            id="hla-take-home"
            label="Annual take-home pay"
            value={annualTakeHome}
            setValue={setAnnualTakeHome}
            wordsValue={takeHomeWords}
            setWordsValue={setTakeHomeWords}
            isDefaultValue={annualTakeHome === defaults.annualTakeHome}
          />
          <EngagerSliderRow
            id="hla-income-growth"
            label="Estimated growth rate of your take-home pay"
            tooltip="Expected yearly growth in take-home pay. Use 0% to stress-test salary stagnation."
            value={incomeGrowthPct}
            setValue={(v) =>
              setIncomeGrowthPct(snapPct(v, maxIncomeGrowth, incomeGrowthStep))
            }
            min={minPct}
            max={maxIncomeGrowth}
            step={incomeGrowthStep}
            format={formatPct}
            variant="inr"
            isDefaultValue={incomeGrowthPct === defaults.incomeGrowthPct}
          />
        </div>
      </section>

      <section className="hla-section hla-section--band-b" aria-labelledby="hla-sec-expenses">
        <SectionHeading
          tipId="hla-sec-expenses-tip"
          tooltip="Include all household costs such as housing, food, transport, and existing EMIs—plus annualized infrequent spends."
        >
          <span id="hla-sec-expenses">
            Now tell us about your current annual household expenses
          </span>
        </SectionHeading>
        <div className="hla-inputs-grid">
          <MoneyField
            id="hla-expenses"
            label="Current annual household expenses"
            value={annualExpenses}
            setValue={setAnnualExpenses}
            wordsValue={expensesWords}
            setWordsValue={setExpensesWords}
            isDefaultValue={annualExpenses === defaults.annualExpenses}
          />
          <EngagerSliderRow
            id="hla-expense-growth"
            label="Estimated annual growth rate of your expenses"
            tooltip="Expected yearly rise in household expenses (inflation and lifestyle)."
            value={expenseGrowthPct}
            setValue={(v) => setExpenseGrowthPct(snapPct(v, maxExpenseGrowth))}
            min={minPct}
            max={maxExpenseGrowth}
            step={pctStep}
            format={formatPct}
            variant="inr"
            isDefaultValue={expenseGrowthPct === defaults.expenseGrowthPct}
          />
        </div>
      </section>

      <section className="hla-section hla-section--band-b" aria-labelledby="hla-sec-home">
        <SectionHeading
          tipId="hla-sec-home-tip"
          tooltip="Moving to your new home may reduce expenses such as rent, but increase others, including maintenance, society charges, lifestyle costs, and commuting. Account for these changes when estimating your future expenses."
        >
          <span id="hla-sec-home">
            Tell us something about the new home you are looking to purchase
          </span>
        </SectionHeading>
        <div className="hla-inputs-grid hla-inputs-grid--home">
          <EngagerSelectRow
            id="hla-move-in"
            label="When do you expect to move into your new home?"
            tooltip="Enter your best estimate of when you expect to move into the new house."
            value={String(moveInYears)}
            onChange={(e) => setMoveInYears(Number(e.target.value))}
            isDefaultValue={moveInYears === defaults.moveInYears}
          >
            {MOVE_IN_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </EngagerSelectRow>

          <SelectWithInlineAmount
            id="hla-post-move-change"
            label="Do you expect your annual household expenses to change after moving into your new home?"
            tooltip="Net change after moving—for example lower rent replaced by higher upkeep, commute, or society charges."
            value={postMoveExpenseChange}
            onChange={setPostMoveExpenseChange}
            options={POST_MOVE_CHANGE_OPTIONS}
            showAmount={showPostMoveAmount}
            amount={postMoveExpenseAmount}
            setAmount={setPostMoveExpenseAmount}
            amountWords={postMoveWords}
            setAmountWords={setPostMoveWords}
            isDefaultSelect={
              postMoveExpenseChange === defaults.postMoveExpenseChange
            }
            isDefaultAmount={
              postMoveExpenseAmount === defaults.postMoveExpenseAmount
            }
          />
        </div>
      </section>

      <section className="hla-section hla-section--band-b" aria-labelledby="hla-sec-life">
        <SectionHeading
          tipId="hla-sec-life-tip"
          tooltip="Examples include having a child, a child starting school or college, supporting ageing parents, or moving from two incomes to one. Such events can permanently increase expenses and reduce household income if one spouse needs to leave their job."
        >
          <span id="hla-sec-life">Life events</span>
        </SectionHeading>
        <div className="hla-inputs-grid hla-inputs-grid--life">
          <EngagerSelectRow
            id="hla-life-event"
            label="Do you expect any major life events in the future?"
            value={hasLifeEvent ? 'Yes' : 'No'}
            onChange={(e) => {
              const next = e.target.value === 'Yes';
              setHasLifeEvent(next);
              if (next) {
                setLifeEventYear(defaults.lifeEventYear);
                setLifeEventExpenseChange(defaults.lifeEventExpenseChange);
                setLifeEventExpenseAmount(defaults.lifeEventExpenseAmount);
                setLifeExpWords(defaults.lifeEventExpenseAmount);
              }
            }}
            isDefaultValue={hasLifeEvent === defaults.hasLifeEvent}
          >
            <option value="No">No</option>
            <option value="Yes">Yes</option>
          </EngagerSelectRow>

          {hasLifeEvent ? (
            <EngagerSelectRow
              id="hla-life-year"
              label="When do you expect this life event to occur?"
              tooltip="Enter your best estimate in years from now."
              value={String(
                Math.min(
                  maxLifeEventYearOptions,
                  Math.max(minLifeEventYear, lifeEventYear),
                ),
              )}
              onChange={(e) => setLifeEventYear(Number(e.target.value))}
              isDefaultValue={lifeEventYear === defaults.lifeEventYear}
            >
              {LIFE_EVENT_YEAR_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </EngagerSelectRow>
          ) : null}

          {hasLifeEvent ? (
            <>
              <div className="hla-life-full-row">
                <SelectWithInlineAmount
                  id="hla-life-expense-change"
                  label="Do you expect this life event to change your annual household expenses?"
                  tooltip="For example school fees, childcare, or elder-care costs."
                  value={lifeEventExpenseChange}
                  onChange={setLifeEventExpenseChange}
                  options={LIFE_CHANGE_OPTIONS}
                  showAmount={showLifeExpAmount}
                  amount={lifeEventExpenseAmount}
                  setAmount={setLifeEventExpenseAmount}
                  amountWords={lifeExpWords}
                  setAmountWords={setLifeExpWords}
                  isDefaultSelect={
                    lifeEventExpenseChange === defaults.lifeEventExpenseChange
                  }
                  isDefaultAmount={
                    lifeEventExpenseAmount === defaults.lifeEventExpenseAmount
                  }
                />
              </div>

              <div className="hla-life-full-row">
                <SelectWithInlineAmount
                  id="hla-life-income-change"
                  label="Do you expect this life event to change your annual household income?"
                  tooltip="For example a partner pausing work (decrease) or a promotion (increase)."
                  value={lifeEventIncomeChange}
                  onChange={setLifeEventIncomeChange}
                  options={LIFE_CHANGE_OPTIONS}
                  showAmount={showLifeIncAmount}
                  amount={lifeEventIncomeAmount}
                  setAmount={setLifeEventIncomeAmount}
                  amountWords={lifeIncWords}
                  setAmountWords={setLifeIncWords}
                  isDefaultSelect={
                    lifeEventIncomeChange === defaults.lifeEventIncomeChange
                  }
                  isDefaultAmount={
                    lifeEventIncomeAmount === defaults.lifeEventIncomeAmount
                  }
                />
              </div>
            </>
          ) : null}
        </div>
      </section>

      <div className="hla-chart-panel" aria-label="Expenses and surplus chart">
        <CalculatorPrecisionToggle className="calc-precision-toggle--inline" />
        <SurplusWaterChart
          years={projection.years}
          title="Surplus income & expenses"
        />
      </div>

      <section className="hla-section hla-section--band-b" aria-labelledby="hla-sec-loan">
        <SectionHeading>
          <span id="hla-sec-loan">Tell us about the loan you are considering</span>
        </SectionHeading>
        <div className="hla-inputs-grid hla-inputs-grid--loan">
          <EngagerSliderRow
            id="hla-tenure"
            label="Loan tenure"
            tooltip="This is the loan tenure used for EMI and for projecting surplus over time."
            value={tenureYears}
            setValue={setTenureYears}
            min={minTenureYears}
            max={maxTenureYears}
            step={1}
            format={formatYearsCount}
            variant="inr"
            isDefaultValue={tenureYears === defaults.tenureYears}
          />

          <EngagerSliderRow
            id="hla-interest"
            label="Annual interest rate"
            tooltip="Illustrative floating home-loan rate used with EMI and loan amount."
            value={interestRatePct}
            setValue={(v) => setInterestRatePct(snapInterestRate(v))}
            min={minInterestRate}
            max={maxInterestRate}
            step={interestRateStep}
            format={formatInterestRatePct}
            variant="inr"
            isDefaultValue={interestRatePct === defaults.interestRatePct}
          />

          <MoneyField
            id="hla-emi"
            label={
              sizeByEmi ? (
                'Maximum EMI you are comfortable paying'
              ) : (
                <>
                  EMI amount{' '}
                  <ModeSwitchLink
                    onClick={() => handleLoanSizingModeChange('emi')}
                  >
                    (enter EMI manually)
                  </ModeSwitchLink>
                </>
              )
            }
            labelAria={
              sizeByEmi
                ? 'Maximum EMI you are comfortable paying'
                : 'EMI amount (enter EMI manually)'
            }
            tooltip={
              sizeByEmi
                ? 'See how your EMI could change your annual surplus over the loan tenure. Try different EMIs to find a safer fit.'
                : 'Monthly EMI implied by the loan amount, interest rate, and tenure. Click “(enter EMI manually)” to type an EMI instead.'
            }
            value={sizeByEmi ? monthlyEmi : resolvedMonthlyEmi}
            setValue={setMonthlyEmi}
            wordsValue={sizeByEmi ? emiWords : resolvedMonthlyEmi}
            setWordsValue={setEmiWords}
            max={maxEmi}
            step={emiStep}
            disabled={!sizeByEmi}
            highlightComputed={!sizeByEmi}
            isDefaultValue={sizeByEmi && monthlyEmi === defaults.monthlyEmi}
          />

          <MoneyField
            id="hla-loan-amount"
            label={
              sizeByEmi ? (
                <>
                  Loan amount{' '}
                  <ModeSwitchLink
                    onClick={() => handleLoanSizingModeChange('loan')}
                  >
                    (enter loan amount manually)
                  </ModeSwitchLink>
                </>
              ) : (
                'Loan amount'
              )
            }
            labelAria={
              sizeByEmi
                ? 'Loan amount (enter loan amount manually)'
                : 'Loan amount'
            }
            tooltip={
              sizeByEmi
                ? 'Loan principal supported by your max EMI, rate, and tenure. Click “(enter loan amount manually)” to type a loan amount instead.'
                : 'Enter the home-loan principal you are considering.'
            }
            value={sizeByEmi ? resolvedLoanAmount : loanAmount}
            setValue={setLoanAmount}
            wordsValue={sizeByEmi ? resolvedLoanAmount : loanWords}
            setWordsValue={setLoanWords}
            max={maxMoney}
            step={moneyStep}
            disabled={sizeByEmi}
            highlightComputed={sizeByEmi}
            isDefaultValue={!sizeByEmi && loanAmount === defaults.loanAmount}
          />
        </div>
      </section>

      <button type="button" className="calc-start-over-btn" onClick={handleStartOver}>
        Start over
      </button>

      <p className="font-small font-light-grey italic margin-0 nps-ups-footnote">
        Illustrative cash-flow stress test. EMI and loan amount are linked by rate and
        tenure. Excludes fees, taxes, rate resets, and lender eligibility rules—not advice.
      </p>
    </div>
  );

  if (embed) return calculatorCard;

  return (
    <div className="calculator-page-layout calculator-page-layout--hla">
      {calculatorCard}
      <div className="calculator-rail-shell">
        <CalculatorToolsRail currentToolKey="home-loan-affordability" />
      </div>
    </div>
  );
}
